import { Dispatch, SetStateAction, useState } from "react";

import { extractDomain } from "@/utils/extractDomain";
import {
	useReplaceProspectForListPersonMutation,
	useSelectProspectForListPersonMutation,
} from "@/pages/Private/redux/list/list-person-prospect.api";

import { ListPerson } from "../../schema/list";
import { isTrackerDummyProspect } from "./dummyProspect";

interface UseFindLeadForProspect {
	companyName: string;
	companyDomain?: string;
	showSearchModal: boolean;
	setShowSearchModal: Dispatch<SetStateAction<boolean>>;
	handleConfirm: (linkedinUrls: string[]) => Promise<void>;
}

// Shared logic behind the dummy-prospect "Select prospect" / "Find leads" flow: resolves the row's
// company (for scoping the global search), opens the search modal, and on confirm replaces the
// placeholder with the chosen person — via the tracker-aware mutation for tracker rows, the plain
// replace otherwise. Used by the table cell (SelectProspectCell), the feed card (NoContactYet) and
// the lead-panel action row (SelectProspectActionButton) so the three stay in lockstep. The row is
// `null` while a host is still loading it (the lead panel); confirming is a no-op then.
export const useFindLeadForProspect = (row: ListPerson | null): UseFindLeadForProspect => {
	const [showSearchModal, setShowSearchModal] = useState(false);

	const [selectProspect] = useSelectProspectForListPersonMutation();
	const [replaceProspect] = useReplaceProspectForListPersonMutation();

	const companyDomain =
		row?.trackerSignalResult?.trackerOrganization?.organization?.primaryDomain ||
		extractDomain(row?.trackerSignalResult?.trackerOrganization?.organization?.websiteUrl) ||
		row?.prospect?.organization?.primaryDomain ||
		extractDomain(row?.prospect?.organization?.websiteUrl);

	const companyName =
		row?.trackerSignalResult?.trackerOrganization?.name ||
		row?.trackerSignalResult?.trackerOrganization?.organization?.name ||
		row?.prospect?.organization?.name ||
		"";

	const handleConfirm = async ([linkedinUrl]: string[]) => {
		if (!linkedinUrl || !row) {
			return;
		}

		const trackerOrganizationId = row.trackerSignalResult?.trackerOrganization?.id;

		if (isTrackerDummyProspect(row) && trackerOrganizationId) {
			await selectProspect({ listPersonId: row.id, linkedinUrl, trackerOrganizationId }).unwrap();
		} else {
			await replaceProspect({ listPersonId: row.id, linkedinUrl }).unwrap();
		}
	};

	return { companyName, companyDomain, showSearchModal, setShowSearchModal, handleConfirm };
};
